import React, { useState, useEffect } from 'react'
import { Card, Form, Input, Button, Avatar, Typography, Space, Divider, App } from 'antd'
import { UserOutlined, SaveOutlined } from '@ant-design/icons'
import { useAuthStore } from '@/stores/authStore'
import { authApi } from '@/services/api'

const { Title, Text } = Typography

interface ProfileForm {
  username: string
  email?: string
  currentPassword?: string
  newPassword?: string
  confirmPassword?: string
}

const ProfilePage: React.FC = () => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const { message } = App.useApp()

  const { user, updateUser } = useAuthStore()

  // 初始化表单数据
  useEffect(() => {
    if (user) {
      window.logger?.info('Initializing form with user data', user)
      form.setFieldsValue({
        username: user.username,
        email: user.email || '',
      })
    }
  }, [user, form])

  const onFinish = async (values: ProfileForm) => {
    try {
      setLoading(true)
      window.logger?.info('Updating user profile', { username: values.username })

      // 分别处理个人资料更新和密码修改
      const hasPasswordChange = values.currentPassword && values.newPassword && values.confirmPassword

      // 更新个人资料
      if (values.username || values.email !== undefined) {
        const profileData = {
          username: values.username,
          email: values.email
        }

        window.logger?.info('Sending profile update request', profileData)
        const profileResponse = await authApi.updateProfile(profileData)
        window.logger?.info('Profile update response', profileResponse)

        // 🔥 修复：正确检查API响应结构
        if (!profileResponse?.success) {
          throw new Error(profileResponse?.message || '更新个人资料失败')
        }

        // 🔥 修复：使用后端返回的最新用户数据更新本地状态
        if (profileResponse.data) {
          updateUser(profileResponse.data)
          window.logger?.info('Local user info updated with server data', profileResponse.data)
        } else {
          // 备用方案：使用表单数据更新
          updateUser({
            username: values.username,
            email: values.email
          })
          window.logger?.info('Local user info updated with form data')
        }
      }

      // 修改密码
      if (hasPasswordChange) {
        const passwordData = {
          currentPassword: values.currentPassword!,
          newPassword: values.newPassword!,
          confirmPassword: values.confirmPassword!
        }

        window.logger?.info('Sending password change request')
        const passwordResponse = await authApi.changePassword(passwordData)
        window.logger?.info('Password change response', passwordResponse)

        // 🔥 修复：正确检查API响应结构
        if (!passwordResponse?.success) {
          throw new Error(passwordResponse?.message || '修改密码失败')
        }
      }

      message.success(hasPasswordChange ? '个人资料和密码更新成功' : '个人资料更新成功')
      form.resetFields(['currentPassword', 'newPassword', 'confirmPassword'])

      window.logger?.info('Profile updated successfully')
    } catch (error: any) {
      window.logger?.error('Profile update failed', error)
      const errorMessage = error.response?.data?.message || error.message || '未知错误'
      message.error('更新失败: ' + errorMessage)
    } finally {
      setLoading(false)
    }
  }



  return (
    <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
      <Card>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Avatar size={80} icon={<UserOutlined />} />
          <Title level={3} style={{ marginTop: '16px', marginBottom: '8px' }}>
            {user?.username || '未知用户'}
          </Title>
          <Text type="secondary">{user?.role || '普通用户'}</Text>
        </div>

        <Divider />

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
        >
          <Title level={4}>基本信息</Title>
          
          <Form.Item
            label="用户名"
            name="username"
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 3, message: '用户名至少3个字符' }
            ]}
          >
            <Input
              placeholder="请输入用户名"
              autoComplete="username"
            />
          </Form.Item>

          <Form.Item
            label="邮箱"
            name="email"
            rules={[
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input
              placeholder="请输入邮箱地址"
              autoComplete="email"
            />
          </Form.Item>

          <Divider />
          <Title level={4}>修改密码</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
            如果不需要修改密码，请留空
          </Text>

              <Form.Item
                label="当前密码"
                name="currentPassword"
              >
                <Input.Password
                  placeholder="请输入当前密码"
                  autoComplete="current-password"
                />
              </Form.Item>

              <Form.Item
                label="新密码"
                name="newPassword"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value && !getFieldValue('currentPassword')) {
                        return Promise.resolve()
                      }
                      if (!value) {
                        return Promise.reject(new Error('请输入新密码'))
                      }
                      if (value.length < 6) {
                        return Promise.reject(new Error('密码至少6个字符'))
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <Input.Password
                  placeholder="请输入新密码"
                  autoComplete="new-password"
                />
              </Form.Item>

              <Form.Item
                label="确认新密码"
                name="confirmPassword"
                dependencies={['newPassword']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const newPassword = getFieldValue('newPassword')
                      if (!newPassword && !value) {
                        return Promise.resolve()
                      }
                      if (!value) {
                        return Promise.reject(new Error('请确认新密码'))
                      }
                      if (value !== newPassword) {
                        return Promise.reject(new Error('两次输入的密码不一致'))
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <Input.Password
                  placeholder="请再次输入新密码"
                  autoComplete="new-password"
                />
              </Form.Item>

          <div style={{ textAlign: 'right', marginTop: '24px' }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SaveOutlined />}
              >
                保存修改
              </Button>
              <Button onClick={() => {
                window.logger?.info('Resetting form to original values', user)
                form.setFieldsValue({
                  username: user?.username,
                  email: user?.email || '',
                })
                form.resetFields(['currentPassword', 'newPassword', 'confirmPassword'])
                message.info('已重置为原始值')
              }}>
                重置
              </Button>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  )
}

export default ProfilePage
